import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';

function Main() {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    daytimeConsumptionAmps: '',
    nightlyConsumptionAmpsPerHour: '',
    batteryHoursNight: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [area, setArea] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const [mapLocation, setMapLocation] = useState(null);

  useEffect(() => {
    // Get area and coordinates from location state
    if (location.state) {
      if (location.state.area) {
        setArea(location.state.area);
      }
      if (location.state.coordinates) {
        setCoordinates(location.state.coordinates);
      }
      if (location.state.location) {
        setMapLocation(location.state.location);
      }
    } else {
      // If no area data, redirect back to home
      navigate('/');
    }
  }, [location, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    // Validate that location is selected (required for city detection)
    if (!mapLocation || !mapLocation.lat || !mapLocation.lng) {
      newErrors.location = 'Please select a location on the map';
    }
    
    // Validate optional numeric fields
    if (formData.daytimeConsumptionAmps && isNaN(parseFloat(formData.daytimeConsumptionAmps))) {
      newErrors.daytimeConsumptionAmps = 'Please enter a valid number';
    }
    
    if (formData.nightlyConsumptionAmpsPerHour && isNaN(parseFloat(formData.nightlyConsumptionAmpsPerHour))) {
      newErrors.nightlyConsumptionAmpsPerHour = 'Please enter a valid number';
    }
    
    if (formData.batteryHoursNight && isNaN(parseFloat(formData.batteryHoursNight))) {
      newErrors.batteryHoursNight = 'Please enter a valid number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    try {
      // City will be auto-detected from lat/lng on the backend
      const requestData = {
        full_name: formData.fullName,
        phone_number: formData.phoneNumber,
        area_sqm: area,
        coordinates: coordinates || [],
        location_lat: mapLocation?.lat || null,
        location_lng: mapLocation?.lng || null,
        // Optional consumption fields
        daytime_consumption_amps: formData.daytimeConsumptionAmps ? parseFloat(formData.daytimeConsumptionAmps) : null,
        nightly_consumption_amps_per_hour: formData.nightlyConsumptionAmpsPerHour ? parseFloat(formData.nightlyConsumptionAmpsPerHour) : null,
        battery_hours_night: formData.batteryHoursNight ? parseFloat(formData.batteryHoursNight) : null,
      };

      const response = await fetch(`${API_BASE_URL}/customer-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('شكراً لك! تم إرسال طلبك بنجاح. سنتواصل معك قريباً عبر الواتساب.');
        // Redirect to home or show success page
        navigate('/', { replace: true });
      } else {
        // Handle validation errors
        if (data.errors) {
          const newErrors = {};
          Object.keys(data.errors).forEach(key => {
            if (key === 'full_name') newErrors.fullName = data.errors[key][0];
            if (key === 'phone_number') newErrors.phoneNumber = data.errors[key][0];
            if (key === 'location_lat' || key === 'location_lng') newErrors.location = 'Please select a location on the map';
            if (key === 'daytime_consumption_amps') newErrors.daytimeConsumptionAmps = data.errors[key][0];
            if (key === 'nightly_consumption_amps_per_hour') newErrors.nightlyConsumptionAmpsPerHour = data.errors[key][0];
            if (key === 'battery_hours_night') newErrors.batteryHoursNight = data.errors[key][0];
          });
          setErrors(newErrors);
        } else {
          alert(data.message || 'حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.');
        }
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('حدث خطأ في الاتصال. يرجى التحقق من الاتصال بالإنترنت والمحاولة مرة أخرى.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!area) {
    return null; // Will redirect in useEffect
  }

  return (
    <main>
      <section className="customer-info-area pt-120 pb-120">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="section-title text-center mb-50">
                <h5>CUSTOMER INFORMATION</h5>
                <h2>Contact Information</h2>
                <p>Please provide your information to complete your request</p>
              </div>
              
              <div className="area-summary mb-50" style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '30px', 
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center'
              }}>
                <h4 className="mb-20">Area Information</h4>
                <p style={{ fontSize: '20px', margin: 0 }}>
                  <strong>Calculated Area:</strong> {area.toFixed(2)} m²
                </p>
              </div>

              <div className="contact-bg02">
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="contact-field p-relative c-name mb-30">
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          placeholder="Full Name"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                        />
                        {errors.fullName && (
                          <span className="error-message" style={{ color: '#dc3545', fontSize: '14px' }}>
                            {errors.fullName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="contact-field p-relative c-email mb-30">
                        <input
                          type="tel"
                          id="phoneNumber"
                          name="phoneNumber"
                          placeholder="Phone Number"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          required
                        />
                        {errors.phoneNumber && (
                          <span className="error-message" style={{ color: '#dc3545', fontSize: '14px' }}>
                            {errors.phoneNumber}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Location Info */}
                    {mapLocation && (
                      <div className="col-lg-12">
                        <div style={{ 
                          backgroundColor: '#e8f5e9', 
                          padding: '15px', 
                          borderRadius: '8px',
                          marginBottom: '20px',
                          border: '1px solid #4caf50'
                        }}>
                          <p style={{ margin: 0, color: '#2e7d32', fontSize: '14px' }}>
                            <i className="fas fa-map-marker-alt"></i> 
                            {' '}الموقع المحدد: {mapLocation.lat.toFixed(6)}, {mapLocation.lng.toFixed(6)}
                            <br />
                            <small style={{ color: '#666' }}>سيتم تحديد المدينة تلقائياً من الموقع</small>
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {errors.location && (
                      <div className="col-lg-12">
                        <span className="error-message" style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginBottom: '15px' }}>
                          {errors.location}
                        </span>
                      </div>
                    )}
                    
                    {/* Optional Consumption Fields */}
                    <div className="col-lg-12">
                      <div className="section-title text-center mb-30" style={{ marginTop: '30px' }}>
                        <h5 style={{ fontSize: '16px', color: '#666' }}>معلومات الاستهلاك (اختياري)</h5>
                      </div>
                    </div>
                    
                    <div className="col-lg-12">
                      <div className="contact-field p-relative c-name mb-30">
                        <input
                          type="number"
                          id="daytimeConsumptionAmps"
                          name="daytimeConsumptionAmps"
                          placeholder="كمية الاستهلاك النهاري بالامبير (اختياري)"
                          value={formData.daytimeConsumptionAmps}
                          onChange={handleInputChange}
                          step="0.1"
                          min="0"
                        />
                        {errors.daytimeConsumptionAmps && (
                          <span className="error-message" style={{ color: '#dc3545', fontSize: '14px' }}>
                            {errors.daytimeConsumptionAmps}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="col-lg-12">
                      <div className="contact-field p-relative c-email mb-30">
                        <input
                          type="number"
                          id="nightlyConsumptionAmpsPerHour"
                          name="nightlyConsumptionAmpsPerHour"
                          placeholder="كمية الاستهلاك الليلي بالساعة الواحدة بالامبير (اختياري)"
                          value={formData.nightlyConsumptionAmpsPerHour}
                          onChange={handleInputChange}
                          step="0.1"
                          min="0"
                        />
                        {errors.nightlyConsumptionAmpsPerHour && (
                          <span className="error-message" style={{ color: '#dc3545', fontSize: '14px' }}>
                            {errors.nightlyConsumptionAmpsPerHour}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="col-lg-12">
                      <div className="contact-field p-relative c-name mb-30">
                        <input
                          type="number"
                          id="batteryHoursNight"
                          name="batteryHoursNight"
                          placeholder="عدد ساعات الاستفادة من البطارية ليلاً (اختياري)"
                          value={formData.batteryHoursNight}
                          onChange={handleInputChange}
                          step="0.5"
                          min="0"
                        />
                        {errors.batteryHoursNight && (
                          <span className="error-message" style={{ color: '#dc3545', fontSize: '14px' }}>
                            {errors.batteryHoursNight}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="col-lg-12">
                      <div className="slider-btn text-center">
                        <button
                          type="submit"
                          className="btn ss-btn active"
                          disabled={submitting}
                        >
                          {submitting ? 'Submitting...' : 'Submit Request'}
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/')}
                          className="btn ss-btn ml-15"
                          style={{ backgroundColor: '#6c757d', border: 'none' }}
                        >
                          Back to Home
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Main;

